import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PrimeurCampaign from '../index';
import styled from 'styled-components';
import Select from '../../../components/Select/SelectFilter';
import Card from '../../../components/Card';
import DatePicker from '../../../components/DatePicker';
import DirectPriceCard from '../components/DirectPriceCard';
import { getPrimeurLive } from '../../../actions/primeur';
import moment from 'moment';
import DirectPmm from '../components/DirectPmm';
import Spinner from '../../../components/Spinner';
import get from 'lodash/get';
import getUrlParams from '../../../utils/getUrlParams';
import { I18n } from 'react-redux-i18n';

const DirectContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 40px;

  .filterContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: 100%;
    justify-content: flex-start;
  }

  .filterContainer:not(:last-children) {
    margin-right: 45px;
  }

  .calendarContainer {
    width: 300px;
    margin-right: 45px;
  }

  .filterCard {
    margin: 0 0 20px;
  }

  @media (max-width: 1300px) and (min-width: 846px) {
    .filterContainer {
      display: grid;
      grid-template-columns: 1fr 1fr;

      > div {
        margin-top: 20px;
      }
    }
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    .filterContainer {
      flex-direction: column;

      > div {
        margin-right: 0 !important;
        margin-top: 20px;
      }
    }
  }
`;

const Direct = () => {
  const [wine, setWine] = useState(null);
  const [vintage, setVintage] = useState(null);
  const [market, setMarket] = useState(null);
  const [date, setDate] = useState(null);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [optionsVintage, setOptionsVintage] = useState([]);

  const primeur = useSelector((state) => state.primeur);
  const dispatch = useDispatch();

  const dispatchGetPrimeurLive = useCallback(
    async (options) => {
      await dispatch(getPrimeurLive(options));
    },
    [dispatch]
  );

  // Initial fetch (replaces componentDidMount)
  useEffect(() => {
    const fetchData = async () => {
      if (window.location.search) {
        const options = getUrlParams(window.location.href);
        await dispatchGetPrimeurLive(options);
      } else {
        await dispatchGetPrimeurLive();
      }
      setLoading(false);
    };
    fetchData();
  }, [dispatchGetPrimeurLive]);

  // Set defaults when data arrives (replaces componentDidUpdate)
  useEffect(() => {
    if (
      primeur?.live?.checks?.length > 0 &&
      primeur.live.checks[0].date !== moment(date).format('YYYY-MM-DD') &&
      primeur?.client_wines?.length > 0 &&
      !wine
    ) {
      const maxVintage = String(
        Math.max(...primeur.client_wines.map((item) => Number(item.vintage)))
      );
      const wineForVintage = primeur.client_wines.find(
        (item) => String(item.vintage) === maxVintage
      );
      const defaultWine = wineForVintage || primeur.client_wines[0];
      setWine({ value: defaultWine.wine, label: defaultWine.wine });
      setVintage({ value: maxVintage, label: maxVintage });
      setOptionsVintage((prev) => [...prev, maxVintage]);
      setDate(moment(primeur.live.checks[0].date));
    }
  }, [primeur]);

  const formatOptions = () => {
    const client_wines = primeur.client_wines || [];
    const live = primeur.live?.checks || [];
    const wineOptions = [...new Set(client_wines.map((item) => item.wine))].map(
      (item) => ({ value: item, label: item })
    );

    let vintageOptions = [];
    if (optionsVintage && optionsVintage.length > 0) {
      vintageOptions = [
        Number(optionsVintage[0]),
        Number(optionsVintage[0]) - 1,
        Number(optionsVintage[0]) - 2
      ].map((item) => ({ value: String(item), label: String(item) }));
    }

    const marketOptions = [
      ...new Set(
        live.map((item) => (item.store_markets ? item.store_markets[0] : ''))
      )
    ].map((item) => ({
      value: item === null ? I18n.t('primeur.errors.market_empty') : item,
      label: item === null ? I18n.t('primeur.errors.market_empty') : item
    }));

    marketOptions.unshift({
      value: 'EUR',
      label: I18n.t('euroCountries'),
      custom: true
    });
    return {
      wine: wineOptions,
      vintage: vintageOptions,
      market: marketOptions
    };
  };

  const getReferenceLineValue = () => {
    const data = primeur.live?.minimum_prices || [];
    const dataStores = primeur.live?.checks || [];
    let currencyCode = '';
    if (market) {
      const store = dataStores.find(
        (item) => get(item, 'store_markets[0]') === market.label
      );
      currencyCode = get(store, 'store_currency_code');
    }
    if (!currencyCode) {
      currencyCode = 'EUR';
    }
    if (wine) {
      return data.find(
        (item) =>
          item.wine_name === wine.label && item.currency_code === currencyCode
      );
    }
    return null;
  };

  const filterData = () => {
    const liveData = primeur.live?.checks || [];

    let data = [...liveData].map((item) => {
      if (item.store_markets && !item.store_markets[0]) {
        item.store_markets[0] = I18n.t('primeur.errors.market_empty');
      }
      return item;
    });

    data = wine
      ? data.filter((item) => item.wine_name === wine.label)
      : data.length > 0
        ? data.filter((item) => item.wine_name === data[0].wine_name)
        : [];
    data = vintage
      ? data.filter((item) => String(item.vintage) === String(vintage.value))
      : data;

    if (market && market.value === 'EUR') {
      data = data.filter((item) => item.currency === 'EUR');
    } else {
      data = market
        ? data.filter((item) => item.store_markets[0] === market.value)
        : data;
    }

    return data;
  };

  const handleChangeDate = async (newDate) => {
    setLoading(true);
    await dispatchGetPrimeurLive({
      date: moment(newDate).format('YYYY-MM-DD')
    });
    setDate(newDate);
    setLoading(false);
  };

  const handleChangeVintage = async (newVintage) => {
    setLoading(true);
    await dispatchGetPrimeurLive({
      vintage: newVintage ? newVintage.value : newVintage
    });
    setVintage(newVintage);
    setLoading(false);
  };

  const filterPmm = () => {
    const data = primeur.live?.minimum_prices || [];
    let filteredPmm = [...data];
    filteredPmm = wine
      ? filteredPmm.filter((item) => item.wine_name === wine.label)
      : [];
    filteredPmm = vintage
      ? filteredPmm.filter(
          (item) => String(item.vintage) === String(vintage.label)
        )
      : [];
    return filteredPmm;
  };

  const options = formatOptions();
  const filteredData = filterData();
  const filteredPmm = filterPmm();
  const referenceLineValue = getReferenceLineValue();

  return (
    <PrimeurCampaign>
      <DirectContainer>
        <Card className="filterCard">
          <div className="filterContainer">
            <Select
              selectedOption={wine}
              onChange={(selectedOption) => setWine(selectedOption)}
              options={options.wine}
              label={I18n.t('primeur.direct.filters.wine')}
              containerStyles={`width:300px;  :not(:last-child){margin-right:45px}`}
            />
            <Select
              selectedOption={vintage}
              onChange={(selectedOption) => handleChangeVintage(selectedOption)}
              options={options.vintage}
              containerStyles={`width:300px; :not(:last-child){margin-right:45px}`}
              label={I18n.t('primeur.direct.filters.vintage')}
            />
            <div className="calendarContainer">
              <DatePicker
                date={date}
                focused={focused}
                onFocusChange={(f) => setFocused(f)}
                onChange={(d) => handleChangeDate(d)}
                label={I18n.t('primeur.direct.filters.date')}
                styles={`
                  .SingleDatePickerInput{
                    border: 0;
                    border-bottom: 1px solid rgba(134,142,150,0.3);
                    height: 25px;
                  } 
                  .DateInput{
                    height: 25px;
                    margin-left: 0px;
                  } 
                  .DateInput_input{
                    height: 25px;
                    border-bottom: 1px solid rgba(134,142,150,0.3);
                    
                  }
                  .SingleDatePicker_picker{
                    margin-left: 40px;
                    top: 35px !important;
                  }

                  .DateInput_fang{
                    top: 25px !important;
                    left: 270px !important;
                  }

                  .DateInput_fangShape{
                    fill: #fbfcfc;
                  }
                `}
              />
            </div>

            <Select
              selectedOption={market}
              placeholder={I18n.t('placeholderAll')}
              onChange={(selectedOption) => setMarket(selectedOption)}
              options={options.market.sort(function (a, b) {
                if (a.value === null) {
                  return -1;
                }
                if (b.value === null) {
                  return 1;
                }
                return a.value.localeCompare(b.value);
              })}
              containerStyles={`width:300px;  max-width:300px; :not(:last-child){margin-right:45px}`}
              label={I18n.t('primeur.direct.filters.market')}
            />
          </div>
        </Card>
        {filteredPmm && filteredPmm.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <DirectPmm data={filteredPmm} />
          </div>
        )}
        <DirectPriceCard
          data={filteredData}
          pmm={filteredPmm}
          wine={wine ? wine.label : ''}
          vintage={vintage ? vintage.label : ''}
          date={date ? moment(date).format('YYYY-MM-DD') : ''}
          referenceLineValue={referenceLineValue}
        />
      </DirectContainer>
      <Spinner loading={loading} />
    </PrimeurCampaign>
  );
};

export default Direct;
